import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import useTodoItems from './useTodoItems';
import useKeyboardInset from './useKeyboardInset';
import CloseIcon from '../Icons/CloseIcon';
import { validateTodoText } from '../utils/validation';

function TodoList({ userInfo }) {
  const { todoItems, loading, error, addTodoItem, removeTodoItem, toggleTodoItem, updateTodoItem } = useTodoItems(userInfo.uid);
  const [inputVal, setInputVal] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [actionError, setActionError] = useState("");
  const [validationError, setValidationError] = useState("");
  const [editValidationError, setEditValidationError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const addInputRef = useRef(null);
  const editInputRef = useRef(null);

  useKeyboardInset();

  const handleEditSubmit = useCallback(async (id) => {
    // Saving bumps createdEditedTime, which the list sorts on, so an untouched todo
    // would jump to the top just from being tapped.
    const current = todoItems.find(item => item.id === id);
    if (!current || current.text === editText) {
      setEditingId(null);
      setEditValidationError("");
      return;
    }

    const validation = validateTodoText(editText);
    if (!validation.valid) {
      setEditValidationError(validation.error);
      return;
    }

    // Leave edit mode before awaiting, so tapping straight into another todo isn't
    // undone when this write resolves. Firestore renders the change locally at once.
    const text = editText;
    setEditingId(null);
    setEditText("");
    setEditValidationError("");

    const result = await updateTodoItem(id, text);
    setActionError(result.success ? "" : result.error);
  }, [editText, todoItems, updateTodoItem]);

  useEffect(() => {
    if (!editingId || !editInputRef.current) return;

    const input = editInputRef.current;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, [editingId]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setEditingId(null);
        setEditText("");
        setEditValidationError("");
      } else if (e.key === 'Enter' && editingId) {
        handleEditSubmit(editingId);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingId, editText, handleEditSubmit]);

  const addItem = async (e) => {
    e.preventDefault();

    const validation = validateTodoText(inputVal);
    if (!validation.valid) {
      setValidationError(validation.error);
      return;
    }

    setValidationError("");
    setIsSubmitting(true);

    const result = await addTodoItem(inputVal);
    setIsSubmitting(false);

    if (result.success) {
      setInputVal("");
      setActionError("");
      addInputRef.current?.focus();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setActionError(result.error);
    }
  };

  const editTodoItem = (id, text) => {
    setEditingId(id);
    setEditText(text);
  };

  const handleDelete = async (id) => {
    const result = await removeTodoItem(id);
    setActionError(result.success ? "" : result.error);
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '20px', color: '#fff' }}>Loading todos...</div>;
  }

  return (
    <>
      {(error || actionError) && (
        <div style={{
          background: '#f44336',
          color: '#fff',
          padding: '12px 20px',
          borderRadius: '8px',
          marginBottom: '16px',
          textAlign: 'center'
        }}>
          {error || actionError}
        </div>
      )}
      <form onSubmit={addItem} id="todoListForm">
        <input
          ref={addInputRef}
          name="addItemInput"
          onChange={e => {
            setInputVal(e.target.value);
            if (validationError) setValidationError("");
          }}
          value={inputVal}
          placeholder="Add item...  📦  🛠 📱"
          className="addItemInput"
          maxLength={500}
        />
        {validationError && (
          <div style={{
            color: '#f44336',
            fontSize: '14px',
            marginTop: '8px',
            marginBottom: '8px'
          }}>
            {validationError}
          </div>
        )}
        <button
          className="mainBtn signOutBtn circleBtn"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'ADDING...' : 'ADD'}
        </button>
      </form>
      {todoItems.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: '#999',
          fontSize: '18px'
        }}>
          No todos yet. Add one above to get started!
        </div>
      ) : (
        todoItems.map(item => (
          <div className="todoList" key={item.id}>
            <input
              type="checkbox"
              id={item.id}
              checked={item.done}
              onChange={() => toggleTodoItem(item.id, !item.done)}
              disabled={isSubmitting}
            />
            <label htmlFor={item.id} />
            {editingId === item.id ? (
              <div className="todoTextEdit">
                <input
                  ref={editInputRef}
                  type="text"
                  name="editItemInput"
                  value={editText}
                  onChange={(e) => {
                    setEditText(e.target.value);
                    if (editValidationError) setEditValidationError("");
                  }}
                  onBlur={() => handleEditSubmit(item.id)}
                  className="todoText"
                  maxLength={500}
                />
                {editValidationError && (
                  <div className="todoTextError">{editValidationError}</div>
                )}
              </div>
            ) : (
              <span
                className="todoText"
                onClick={() => editTodoItem(item.id, item.text)}
              >
                {item.text}
              </span>
            )}
            <div className="todoListButtons">
              <button
                className="closeButton"
                onClick={() => handleDelete(item.id)}
                disabled={isSubmitting}
                aria-label="Delete todo"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        ))
      )}
    </>
  );
}

TodoList.propTypes = {
  userInfo: PropTypes.shape({
    uid: PropTypes.string.isRequired,
  }).isRequired,
};

export default TodoList;